import { View } from "@carbon/icons-react";
import {
  Checkbox,
  ContentSwitcher,
  Heading,
  Layer,
  Popover,
  PopoverContent,
  Section,
  Switch,
  usePrefix,
} from "@carbon/react";
import React, { useState } from "react";

import { useT } from "src/internationalization";

import { AlgorithmViewType, ViewModeType } from "../relatedCases.typings";

// TODO: set to false to test a new algorithm
const DISABLE_ALGORITHM_CHOICE = true;

export type ViewModePopoverProps = {
  isOpen: boolean;
  onOpenState: (open: boolean) => void;
  selectedAlgorithm: AlgorithmViewType;
  onSelectedAlgorithm: (algorithm: AlgorithmViewType) => void;
  mergeRelatedCases: boolean;
  onMergeRelatedCases: (merge: boolean) => void;
  selectedViewMode: ViewModeType;
  onSelectViewMode: (mode: ViewModeType) => void;
};

export const useViewModePopover = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<AlgorithmViewType>("New");
  const [mergeRelatedCases, setMergeRelatedCases] = useState(false);
  const [selectedViewMode, setSelectedViewMode] = useState<ViewModeType>("Table");

  return {
    isPopoverOpen,
    setIsPopoverOpen,
    selectedAlgorithm,
    setSelectedAlgorithm,
    mergeRelatedCases,
    setMergeRelatedCases,
    selectedViewMode,
    setSelectedViewMode,
  };
};

const ViewModePopover: React.FC<ViewModePopoverProps> = ({
  isOpen,
  onOpenState,
  selectedAlgorithm,
  onSelectedAlgorithm,
  mergeRelatedCases,
  onMergeRelatedCases,
  selectedViewMode,
  onSelectViewMode,
}) => {
  const prefix = usePrefix();
  const t = useT();

  return (
    <Layer>
      <Popover
        open={isOpen}
        isTabTip
        onRequestClose={() => onOpenState(false)}
        align="bottom-right"
      >
        <button
          aria-label="ViewMode"
          type="button"
          onClick={() => {
            onOpenState(!isOpen);
          }}
          className={`${prefix}--toolbar-action ${prefix}--overflow-menu`}
        >
          <View />
        </button>
        <PopoverContent>
          <div className="flex flex-col gap-y-7">
            <div className="flex flex-col p-5 pb-7 gap-y-5">
              <Section level={5}>
                <Heading>{t("widget.relatedCases.viewModePopOverTitle")}</Heading>
              </Section>
              <div className="text-label01">
                {t("widget.relatedCases.switchViewModeInputLabel")}
              </div>
              <ContentSwitcher
                style={{ width: "300px" }}
                size="sm"
                selectedIndex={selectedViewMode === "Table" ? 0 : 1}
                onChange={(data) => {
                  onSelectViewMode(data.name as ViewModeType);
                }}
              >
                <Switch name="Table" text={t("widget.relatedCases.switchViewModeTable")} />
                <Switch name="Graph" text={t("widget.relatedCases.switchViewModeGraph")} />
              </ContentSwitcher>

              {selectedViewMode === "Graph" && !DISABLE_ALGORITHM_CHOICE && (
                <div className="flex flex-col gap-5">
                  <div className="text-label01">{t("widget.relatedCases.algorithm.label")}</div>
                  <ContentSwitcher
                    style={{ width: "300px" }}
                    size="sm"
                    selectedIndex={selectedAlgorithm === "Old" ? 0 : 1}
                    onChange={(data) => {
                      onSelectedAlgorithm(data.name as AlgorithmViewType);
                    }}
                  >
                    <Switch name="Old" text={t("widget.relatedCases.algorithm.old")} />
                    <Switch name="New" text={t("widget.relatedCases.algorithm.new")} />
                  </ContentSwitcher>
                </div>
              )}

              {selectedViewMode === "Graph" && (
                <div className="flex flex-col gap-5">
                  <div className="text-label01">
                    {t("widget.relatedCases.mergeRelatedCases.checkbox.label")}
                  </div>
                  <Checkbox
                    id="merge-requests"
                    labelText={t("widget.relatedCases.mergeRelatedCases.checkbox.label")}
                    defaultChecked={false}
                    onChange={() => {
                      onMergeRelatedCases(!mergeRelatedCases);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </Layer>
  );
};

export default ViewModePopover;
